/**
 * <cw-shelf>: the essentials shelf. A port of Shopping v13's shelf(), nextUp() and runSq()
 * (design-handoff/prototype, option 1j) onto real pages.
 *
 * What it does (behaviour.md §2, specs/essentials-shelf-spec.md §4-5):
 * - A jar fills when the basket holds anything from its aisle. It reads the basket only,
 *   never the aisle that is open.
 * - The squirrel stands on the suggested aisle's jar and says why. The suggestion scores each
 *   missing aisle by its pairings with the aisles already in the basket (3 meal, 2 occasion,
 *   1 general); highest total wins, ties go to shelf order.
 * - The target is sticky: it changes only when (a) its aisle gets an item, (b) it has been
 *   ignored twice (two other aisles got their first item), or (c) a clearly better pairing
 *   appears (strength 3 and at least 2 points more). At most one re-rank per add.
 * - When the target gets its first item: "Atta is in ✓" for 1.5 s, then the next suggestion.
 *   The squirrel moves there when the shopper leaves the aisle or taps the bubble.
 * - It hops on every quantity increase, never on a decrease. Reduced motion: no hop, no
 *   travel, it is simply at its jar.
 *
 * The prototype is one page; the shop is many. So the squirrel's memory (target, what it is
 * waiting on, which aisles it gave up on, where it stands) lives in sessionStorage for the
 * visit, and the last thing it showed is restored before paint by snippets/cw-shelf.liquid.
 *
 * One deliberate difference from v13, logged in open-questions.md: v13 re-keys the hop on the
 * basket total, which also replays it on a decrease. The sheets and the checklist say "none
 * on decrease", so the hop listens to increases only.
 */
import { cart } from '@cw/cart';
import { AISLES, IDS, aisleOf, isOpen } from '@cw/aisles';

const STATE_KEY = 'cw:shelf';
const VIEW_KEY = 'cw:shelf:view';

const read = (id) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || 'null');
  } catch {
    return null;
  }
};
const load = (key, fallback) => {
  try {
    return JSON.parse(sessionStorage.getItem(key) || 'null') ?? fallback;
  } catch {
    return fallback;
  }
};
const save = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or storage full: the shelf still works for this page.
  }
};

const COPY = read('cw-shelf-copy') || { pairings: [], pantry: {}, start: {}, in_aisle: {} };
const N = IDS.length;
const idx = (id) => Math.max(0, IDS.indexOf(id));
const short = (id) => AISLES.find((a) => a.id === id)?.short || '';
const urlOf = (id) => AISLES.find((a) => a.id === id)?.url || '/collections';

/** Which aisles the basket covers. */
function basketAisles() {
  const has = {};
  for (const productId of cart.productsIn()) {
    const aisle = aisleOf(productId);
    if (aisle) has[aisle] = true;
  }
  return has;
}

/**
 * The prototype's nextUp(): which missing aisle to suggest, and the words for it.
 * forceId asks for the words for one aisle; skip lists aisles given up on this visit.
 */
function nextUp(has, forceId = null, skip = []) {
  const n = IDS.filter((id) => has[id]).length;
  if (n === N && !forceId) return { allDone: true };
  if (n === 0 && forceId && forceId !== 'atta') return { nextId: forceId, nextCopy: COPY.pantry[forceId] };
  if (n === 0) {
    // Atta first, unless it is all sold out: then the first aisle that has something.
    const first = isOpen('atta') ? 'atta' : IDS.find(isOpen) || 'atta';
    return { nextId: first, nextCopy: first === 'atta' ? COPY.start.atta : COPY.pantry[first] };
  }

  // Never an aisle that is all sold out, unless nothing else is missing.
  let cands = forceId ? IDS.filter((id) => id === forceId) : IDS.filter((id) => !has[id] && !skip.includes(id) && isOpen(id));
  if (!cands.length) cands = IDS.filter((id) => !has[id] && isOpen(id));
  if (!cands.length) cands = IDS.filter((id) => !has[id]);
  const ranked = cands
    .map((id, ci) => {
      let score = 0;
      let best = null;
      for (const link of COPY.pairings) {
        if (link.to === id && has[link.from]) {
          score += link.strength;
          if (!best || link.strength > best.strength) best = link;
        }
      }
      return { id, score, best, ci };
    })
    .sort((a, b) => b.score - a.score || a.ci - b.ci);

  const t = ranked[0];
  const near = n >= N - 2;
  let copy;
  if (t.best && t.best.strength >= 3) copy = t.best.copy;
  else if (t.best && t.best.strength >= 2 && !near) copy = t.best.copy;
  else copy = near ? COPY.still_missing.replace('{aisle}', short(t.id)) : COPY.pantry[t.id];
  return { nextId: t.id, nextCopy: copy, score: t.score, str: t.best ? t.best.strength : 0 };
}

/** The squirrel's memory for this visit. */
const S = Object.assign(
  { target: null, pending: null, pendAt: 0, pos: null, skip: [], ignored: 0, prevHas: null },
  load(STATE_KEY, {})
);
/** What to play next; not remembered across pages. */
const T = { hopPending: false, celebrate: false, nod: null, anim: false };
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The prototype's shelf(): move the sticky target on, then decide what the bubble says. */
function reconcile(current) {
  const has = basketAisles();
  const n = IDS.filter((id) => has[id]).length;
  const allDone = n === N;
  const handled = {};

  if (allDone) {
    if (S.target !== '__done') {
      S.target = '__done';
      S.pending = null;
      T.celebrate = true;
    }
  } else {
    // A target that sold out since it was picked (and is not in the basket) is picked again.
    if (!S.target || S.target === '__done' || (!has[S.target] && !isOpen(S.target))) {
      S.target = nextUp(has, null, S.skip).nextId;
      S.pending = null;
      S.ignored = 0;
    }
    for (let guard = 0; guard < 10; guard++) {
      if (has[S.target]) {
        handled[S.target] = true;
        if (!S.pending) {
          S.pending = nextUp(has, null, S.skip).nextId;
          S.pendAt = Date.now();
          T.hopPending = true;
          clearTimeout(reconcile.timer);
          reconcile.timer = setTimeout(renderAll, 1520);
        }
      } else if (S.pending) {
        S.pending = null;
      }
      if (S.pending && current !== S.target) {
        S.target = S.pending;
        S.pending = null;
        S.ignored = 0;
        continue;
      }
      break;
    }
  }

  // First item from a different aisle: nod at it, and maybe re-rank (once per add).
  const prev = S.prevHas;
  S.prevHas = { ...has };
  if (prev && !allDone) {
    const fresh = IDS.filter((id) => has[id] && !prev[id] && !handled[id] && id !== S.target);
    if (fresh.length) {
      T.nod = idx(fresh[fresh.length - 1]);
      if (!S.pending) {
        S.ignored = (S.ignored || 0) + 1;
        if (S.ignored >= 2) {
          S.skip.push(S.target);
          const r = nextUp(has, null, S.skip);
          if (r.nextId) S.target = r.nextId;
          S.ignored = 0;
        } else {
          const cur = nextUp(has, S.target);
          const best = nextUp(has, null, S.skip);
          if (best.nextId && best.nextId !== S.target && best.str >= 3 && (best.score || 0) >= (cur.score || 0) + 2) {
            S.target = best.nextId;
            S.ignored = 0;
          }
        }
      }
    }
  }

  const goal = allDone ? 4 : idx(S.target);
  if (S.pos === null) S.pos = goal;

  // Bubble
  let copy = '';
  let arrow = false;
  let go = null;
  if (allDone) {
    copy = COPY.done;
  } else if (S.pending) {
    go = 'pending';
    if (Date.now() - S.pendAt < 1500) {
      copy = `${short(S.target)} is in ✓`;
    } else {
      copy = nextUp(has, S.pending).nextCopy;
      arrow = true;
    }
  } else if (current === S.target) {
    copy = COPY.in_aisle[S.target] || `Pick your ${short(S.target).toLowerCase()}`;
  } else {
    copy = nextUp(has, S.target).nextCopy;
    arrow = true;
    go = S.target;
  }
  const hl = allDone ? null : S.pending || S.target;

  save(STATE_KEY, S);
  return { has, n, goal, copy, arrow, go, hl };
}

/** Squirrel standing at jar i. */
const xOf = (i) => i * 40 + 2;
const faceAtRest = (pos) => (pos > 3 ? 1 : -1);

class CwShelf extends HTMLElement {
  #view = null;
  #visible = true;
  #observer = null;

  connectedCallback() {
    this.dataset.ready = '';
    this.sq = this.querySelector('.cw-shelf__sq');
    this.hopEl = this.querySelector('.cw-shelf__hop');
    this.img = this.querySelector('.cw-shelf__img');
    this.bubble = this.querySelector('.cw-shelf__bubble');
    this.copyEl = this.querySelector('.cw-shelf__copy');
    this.arrowEl = this.querySelector('.cw-shelf__arrow');
    this.countEl = this.querySelector('.cw-shelf__count');
    this.jars = [...this.querySelectorAll('.cw-shelf__jar')];
    this.labels = [...this.querySelectorAll('.cw-shelf__label')];

    this.bubble.addEventListener('click', this.#onBubble);
    // Never animate while the shelf is out of sight; the move plays when it comes back.
    // A copy that was hidden (the other size's shelf) catches up when it is shown.
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.render();
    });
    this.#observer.observe(this);
    if (shown(this)) this.render();
    // The title row folds by its real height (cw-shelf.css --cw-trim-h); a shelf that starts
    // hidden (in a shut panel) is measured the first time it renders in view.
    this.measure();
    document.fonts?.ready.then(() => this.measure());
  }

  measure() {
    const trim = this.querySelector('.cw-shelf__trim');
    if (trim?.scrollHeight) this.style.setProperty('--cw-trim-h', `${trim.scrollHeight}px`);
  }

  disconnectedCallback() {
    this.bubble?.removeEventListener('click', this.#onBubble);
    this.#observer?.disconnect();
  }

  render() {
    const v = reconcile(this.dataset.current || '');
    this.#view = v;
    if (!this.style.getPropertyValue('--cw-trim-h')) this.measure();

    this.jars.forEach((jar, i) => {
      jar.classList.toggle('is-filled', !!v.has[IDS[i]]);
      jar.classList.toggle('is-next', IDS[i] === v.hl);
    });
    this.labels.forEach((label, i) => {
      label.classList.toggle('is-filled', !!v.has[IDS[i]]);
      label.classList.toggle('is-next', IDS[i] === v.hl);
    });
    if (this.countEl) {
      // Order Again shows "0/9" too (v13 reorder3's count).
      this.countEl.hidden = v.n === 0 && !this.hasAttribute('data-fixed');
      this.countEl.textContent = `${v.n}/${N}`;
    }

    this.copyEl.textContent = v.copy;
    this.arrowEl.hidden = !v.arrow;
    this.bubble.classList.toggle('is-static', !v.go);
    this.bubble.setAttribute('aria-label', v.go ? `${v.copy}. Open ${AISLES[idx(v.go === 'pending' ? S.pending : v.go)]?.name || ''}` : v.copy);
    this.#placeBubble();
    this.bubble.hidden = false;

    if (!T.anim) {
      this.style.setProperty('--cw-sq-x', `${xOf(S.pos)}px`);
      this.img.style.transform = `scaleX(${faceAtRest(S.pos)})`;
    }
    this.#move(v.goal);
    save(VIEW_KEY, { pos: S.pos, copy: v.copy, arrow: v.arrow, hl: v.hl });
    // Order Again outlines the card or tile of the aisle the squirrel is on.
    this.dispatchEvent(new CustomEvent('cw:shelf:render', { bubbles: true, detail: { hl: v.hl } }));
  }

  /** The bubble follows the squirrel's resting place (it moves after the squirrel lands). */
  #placeBubble() {
    const bx = xOf(S.pos);
    const right = S.pos <= 3;
    this.bubble.classList.toggle('is-right', right);
    this.bubble.classList.toggle('is-left', !right);
    if (right) {
      this.bubble.style.left = `${bx + 35}px`;
      this.bubble.style.right = '';
      this.bubble.style.maxWidth = `${354 - (bx + 35)}px`;
    } else {
      this.bubble.style.left = '';
      this.bubble.style.right = `${354 - (bx - 5)}px`;
      this.bubble.style.maxWidth = `${bx - 5}px`;
    }
  }

  /** The prototype's runSq(): hop in place, nod, travel jar by jar, celebrate. */
  #move(goal) {
    if (T.anim || !this.#visible || !this.isConnected || this.hasAttribute('data-away')) return;
    const cnt = Math.abs(goal - S.pos);
    const dir = Math.sign(goal - S.pos);
    if (!cnt && !T.hopPending && !T.celebrate && T.nod === null) return;

    if (reduceMotion() || !this.sq.animate) {
      S.pos = goal;
      T.hopPending = T.celebrate = false;
      T.nod = null;
      save(STATE_KEY, S);
      this.render();
      return;
    }

    const segs = [];
    if (T.hopPending) segs.push({ f: S.pos, t: S.pos, h: 11, d: 320 });
    const hadNod = T.nod !== null;
    const nodDir = hadNod ? Math.sign(T.nod - S.pos) : 0;
    let nodEnd = 0;
    if (hadNod) {
      nodEnd = segs.reduce((t, s) => t + s.d, 0);
      segs.push({ f: S.pos, t: S.pos, h: 4, d: 180 }, { f: S.pos, t: S.pos, h: 3, d: 170 });
      nodEnd += 350;
    }
    const per = cnt ? Math.min(150, 900 / cnt) : 0;
    for (let i = 0; i < cnt; i++) segs.push({ f: S.pos + i * dir, t: S.pos + (i + 1) * dir, h: 8, d: per });
    if (T.celebrate) segs.push({ f: goal, t: goal, h: 12, d: 340 });

    const total = segs.reduce((t, s) => t + s.d, 0);
    const frames = [{ transform: `translate(${xOf(S.pos)}px, 0px)`, offset: 0, easing: 'ease-out' }];
    let t = 0;
    for (const s of segs) {
      const mid = (xOf(s.f) + xOf(s.t)) / 2;
      frames.push({ transform: `translate(${mid}px, ${-s.h}px)`, offset: (t + s.d / 2) / total, easing: 'ease-in' });
      t += s.d;
      frames.push({ transform: `translate(${xOf(s.t)}px, 0px)`, offset: t / total, easing: 'ease-out' });
    }
    frames[frames.length - 1].offset = 1;

    T.anim = true;
    T.hopPending = T.celebrate = false;
    T.nod = null;
    const pose = (d) => `scaleX(${d > 0 ? -1 : d < 0 ? 1 : faceAtRest(goal)})`;
    if (hadNod && nodDir) {
      this.img.style.transform = pose(nodDir);
      setTimeout(() => (this.img.style.transform = pose(dir)), nodEnd);
    } else if (dir) {
      this.img.style.transform = pose(dir);
    }

    const animation = this.sq.animate(frames, { duration: total, fill: 'forwards' });
    animation.onfinish = () => {
      S.pos = goal;
      this.style.setProperty('--cw-sq-x', `${xOf(goal)}px`);
      this.img.style.transform = pose(0);
      animation.cancel();
      T.anim = false;
      save(STATE_KEY, S);
      this.render();
    };
  }

  /** A small hop in place on every increase (cwhop, 420 ms). */
  hop() {
    if (reduceMotion()) return;
    this.hopEl.classList.remove('is-hopping');
    void this.hopEl.offsetWidth; // restart the animation
    this.hopEl.classList.add('is-hopping');
  }

  #onBubble = () => {
    const v = this.#view;
    if (!v || !v.go) return;
    let target = v.go;
    if (target === 'pending') {
      target = S.pending;
      S.target = S.pending;
      S.pending = null;
      save(STATE_KEY, S);
    }
    if (!target) return;
    // A screen that shows the aisle itself (Order Again: its card or tile) takes the tap.
    const go = new CustomEvent('cw:shelf:go', { bubbles: true, cancelable: true, detail: { aisle: target } });
    this.dispatchEvent(go);
    if (go.defaultPrevented) renderAll();
    else window.location.href = urlOf(target);
  };
}

/**
 * Only a shelf the shopper can see decides anything. A hidden copy (the panel's while it is
 * shut, and always on phones; the other size's shelf) has no aisle open, so it would read the
 * shopper as having left the aisle and send the squirrel on to the next jar.
 */
const shown = (shelf) => shelf.getClientRects().length > 0;

function renderAll() {
  for (const shelf of document.querySelectorAll('cw-shelf')) if (shown(shelf)) shelf.render();
}

document.addEventListener('cw:cart:change', renderAll);

// A panel with its own shelf (options, basket at 600+) takes the squirrel while it is open.
const WIDE = window.matchMedia('(min-width: 600px)');
document.addEventListener('cw:sheet:open', (event) => {
  const own = event.detail.sheet.querySelector('cw-shelf');
  if (!own || !WIDE.matches) return;
  const page = [...document.querySelectorAll('cw-shelf')].filter((shelf) => !event.detail.sheet.contains(shelf));
  // Over an aisle page the panel's squirrel knows that aisle is open; Order Again is no aisle.
  if (!own.hasAttribute('data-fixed')) own.dataset.current = page[0]?.dataset.current || '';
  for (const shelf of page) shelf.toggleAttribute('data-away', true);
  own.render();
});
document.addEventListener('cw:sheet:close', () => {
  for (const shelf of document.querySelectorAll('cw-shelf[data-away]')) {
    shelf.removeAttribute('data-away');
    shelf.render();
  }
});
document.addEventListener('cw:cart:increase', () => {
  for (const shelf of document.querySelectorAll('cw-shelf')) shelf.hop();
});

if (!customElements.get('cw-shelf')) customElements.define('cw-shelf', CwShelf);
